/**
 * FilesPage Component
 * Main files page orchestrating all file management components
 */

import {
  html,
  attr,
  prop,
  Signal,
  When,
  on,
  computedOf,
  OnDispose,
  Fragment,
  Async,
} from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Button, ButtonVariant, Icon } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { DataTable } from '@/components/data'
import { EmptyState, LoadingState, ErrorState } from '@/components/ui'
import {
  FolderTree,
  FileCard,
  FolderPickerModal,
  BulkActionsBar,
  FilesToolbar,
} from '@/components/files'
import { Workspace } from '@/state/workspace'
import { getFileIcon, getFileIconBg } from '@/utils'
import { createFilesState, type FileDisplayItem } from './use-files-state'

// ============================================================================
// Files Page Component
// ============================================================================

export interface FilesPageOptions {
  projectId: Signal<string>
}

export function FilesPage({ projectId }: FilesPageOptions) {
  projectId.on(Workspace.changeProject)

  // Initialize state and handlers
  const { state, handlers, initialLoad } = createFilesState(projectId)

  return PageLayout({
    header: 'Files',
    addPadding: false,
    content: Fragment(
      attr.class('flex h-full w-full overflow-hidden'),
      // Cleanup debounce timer on dispose
      OnDispose(() => {
        handlers.cleanupSearchDebounce()
      }),
      // Folder sidebar
      html.div(
        attr.class(
          state.sidebarCollapsed.map((collapsed) =>
            collapsed
              ? 'w-0 overflow-hidden transition-all duration-200'
              : 'w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 transition-all duration-200'
          )
        ),
        FolderTree({
          selectedFolderId: state.selectedFolderId,
          onSelect: handlers.handleFolderSelect,
          onFolderCreated: () => handlers.loadFiles(),
          refreshTrigger: state.folderTreeRefresh,
          projectId,
        })
      ),
      // Main content area
      html.div(
        attr.class('flex-1 min-w-0 flex flex-col h-full overflow-hidden'),
        // Toolbar
        FilesToolbar({
          sidebarCollapsed: state.sidebarCollapsed,
          onToggleSidebar: () =>
            state.sidebarCollapsed.set(!state.sidebarCollapsed.get()),
          folderPath: state.folderPath,
          onNavigate: handlers.handleFolderSelect,
          searchQuery: state.searchQuery,
          onSearchChange: handlers.handleSearchChange,
          includeSubfolders: state.includeSubfolders,
          onIncludeSubfoldersChange: handlers.handleIncludeSubfoldersChange,
          sortBy: state.sortBy,
          onSortByChange: (field) => {
            state.sortBy.set(field)
            handlers.loadFiles()
          },
          sortOrder: state.sortOrder,
          onToggleSortOrder: () => {
            state.sortOrder.set(
              state.sortOrder.get() === 'asc' ? 'desc' : 'asc'
            )
            handlers.loadFiles()
          },
          uploading: state.uploading,
          uploadProgress: state.uploadProgress,
          uploadingCount: state.uploadingCount,
          uploadedCount: state.uploadedCount,
          onUploadClick: handlers.triggerFileInput,
        }),
        // Hidden file input (supports multiple files)
        html.input(
          attr.type('file'),
          attr.id('file-upload-input'),
          attr.class('hidden'),
          attr.multiple(true),
          on.change((e) => {
            const input = e.target as HTMLInputElement
            if (input.files && input.files.length > 0) {
              handlers.handleUpload(input.files)
              input.value = '' // Reset for next upload
            }
          })
        ),
        // Main file list area
        Async(initialLoad, {
          pending: () =>
            html.div(
              attr.class('flex-1 p-6 h-full overflow-hidden'),
              LoadingState({ message: 'Loading files...', size: 'lg' })
            ),
          error: (err) =>
            html.div(
              attr.class('flex-1 p-6 h-full overflow-hidden'),
              ErrorState({
                error: prop<string | null>(
                  err instanceof Error ? err.message : 'Failed to load files'
                ),
              })
            ),
          then: () =>
            html.div(
              attr.class('flex-1 p-6 h-full overflow-hidden'),
              html.div(
                attr.class('space-y-4 h-full overflow-hidden'),
                // Selection toolbar (always visible when there are files)
                When(
                  state.hasNoFiles.map((noFiles) => !noFiles),
                  () =>
                    html.div(
                      attr.class(
                        'bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-4'
                      ),
                      // Select all checkbox
                      html.label(
                        attr.class('flex items-center gap-2 cursor-pointer'),
                        html.input(
                          attr.type('checkbox'),
                          attr.class(
                            'w-4 h-4 text-brand-600 bg-gray-100 border-gray-300 rounded focus:ring-brand-500 cursor-pointer'
                          ),
                          attr.checked(state.allFilesSelected),
                          on.change(handlers.toggleSelectAll)
                        ),
                        html.span(
                          attr.class('text-sm text-gray-600'),
                          computedOf(
                            state.selectedCount,
                            state.files
                          )((count, fileList) =>
                            count > 0
                              ? `${count} of ${fileList.length} selected`
                              : 'Select all'
                          )
                        )
                      )
                    )
                ),
                // Bulk actions toolbar
                When(state.hasSelectedFiles, () =>
                  BulkActionsBar({
                    selectedCount: state.selectedCount,
                    performingAction: state.performingBulkAction,
                    onClearSelection: handlers.clearSelection,
                    onMove: handlers.openBulkMoveModal,
                    onCopy: handlers.openBulkCopyModal,
                    onDelete: handlers.handleBulkDelete,
                  })
                ),
                // Bulk action error message
                When(state.hasBulkActionError, () =>
                  html.div(
                    attr.class(
                      'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm whitespace-pre-wrap'
                    ),
                    state.bulkActionError
                  )
                ),
                // Upload error message
                When(state.hasUploadError, () =>
                  html.div(
                    attr.class(
                      'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm whitespace-pre-wrap'
                    ),
                    state.uploadError
                  )
                ),
                // Files table/cards
                html.div(
                  attr.class(
                    'bg-white rounded-xl border border-gray-200 overflow-hidden'
                  ),
                  When(
                    state.hasNoFiles,
                    () =>
                      EmptyState({
                        iconName: state.hasSearchQuery.map(
                          (hasSearch): string =>
                            hasSearch ? 'mdi:magnify' : 'mdi:folder-open'
                        ),
                        title: computedOf(
                          state.hasSearchQuery,
                          state.selectedFolderId
                        )((hasSearch, folderId): string => {
                          if (hasSearch) return 'No files found'
                          return folderId
                            ? 'This folder is empty'
                            : 'No files yet'
                        }),
                        description: computedOf(
                          state.hasSearchQuery,
                          state.selectedFolderId,
                          state.includeSubfolders
                        )((hasSearch, folderId, withSubfolders): string => {
                          if (hasSearch) {
                            return withSubfolders
                              ? 'Try a different search term or check other folders.'
                              : 'Try a different search term or enable "Include subfolders".'
                          }
                          return folderId
                            ? 'Upload files to this folder to get started.'
                            : 'Upload your first file to get started.'
                        }),
                        action: When(
                          state.hasSearchQuery.map((hs) => !hs),
                          () =>
                            Button(
                              { onClick: handlers.triggerFileInput },
                              Icon(
                                { icon: 'mdi:cloud-upload' },
                                attr.class('w-4 h-4 mr-2')
                              ),
                              'Upload File'
                            )
                        ),
                      }),
                    () =>
                      html.div(
                        attr.class('overflow-y-auto'),
                        DataTable<FileDisplayItem>({
                          columns: [
                            {
                              key: 'checkbox',
                              header: '',
                              render: (item) =>
                                html.input(
                                  attr.type('checkbox'),
                                  attr.class(
                                    'w-4 h-4 text-brand-600 bg-gray-100 border-gray-300 rounded focus:ring-brand-500 cursor-pointer'
                                  ),
                                  attr.checked(
                                    state.selectedFileHashes.map((s) =>
                                      s.has(item.get().hash)
                                    )
                                  ),
                                  on.change(() =>
                                    handlers.toggleFileSelection(
                                      item.get().hash
                                    )
                                  )
                                ),
                            },
                            {
                              key: 'name',
                              header: 'Name',
                              render: (item) =>
                                html.div(
                                  attr.class('flex items-center gap-3'),
                                  html.div(
                                    attr.class(
                                      item.map(
                                        (f) =>
                                          `w-10 h-10 rounded-lg flex items-center justify-center ${getFileIconBg(f.fileType)}`
                                      )
                                    ),
                                    Icon(
                                      {
                                        icon: item.map((f) =>
                                          getFileIcon(f.fileType)
                                        ),
                                      },
                                      attr.class('w-5 h-5')
                                    )
                                  ),
                                  html.div(
                                    html.p(
                                      attr.class('font-medium text-gray-900'),
                                      item.map((f) => f.name)
                                    ),
                                    html.p(
                                      attr.class('text-sm text-gray-500'),
                                      item.map((f) => f.mimeType)
                                    )
                                  )
                                ),
                            },
                            {
                              key: 'size',
                              header: 'Size',
                              className: 'text-gray-500 text-sm',
                            },
                            {
                              key: 'createdAt',
                              header: 'Uploaded',
                              className: 'text-gray-500 text-sm',
                            },
                            {
                              key: 'hash',
                              header: '',
                              render: (item) =>
                                Anchor(
                                  {
                                    href: computedOf(
                                      item,
                                      projectId
                                    )(
                                      (f, pid) =>
                                        Workspace.projectUrl(
                                          `/files/${f.hash}`,
                                          pid
                                        ) ?? '#'
                                    ),
                                  },
                                  attr.class(
                                    'text-brand-600 hover:text-brand-700 text-sm font-medium'
                                  ),
                                  'View'
                                ),
                            },
                          ],
                          data: state.files,
                          renderMobileCard: (file) =>
                            FileCard({
                              file,
                              selectedFileHashes: state.selectedFileHashes,
                              onToggleSelection: handlers.toggleFileSelection,
                              projectId,
                            }),
                          emptyMessage:
                            'No files yet. Upload one to get started.',
                        }),
                        // Load More button
                        When(state.hasMorePages, () =>
                          html.div(
                            attr.class('px-6 py-4 border-t border-gray-200'),
                            Button(
                              {
                                variant: 'ghost' as ButtonVariant,
                                disabled: state.loadingMore,
                                onClick: () =>
                                  handlers.loadFiles(state.nextCursor.get()),
                              },
                              When(state.loadingMore, () =>
                                Icon(
                                  { icon: 'mdi:loading' },
                                  attr.class('w-4 h-4 mr-2 animate-spin')
                                )
                              ),
                              state.loadingMore.map((l) =>
                                l ? 'Loading...' : 'Load More'
                              )
                            )
                          )
                        )
                      )
                  )
                )
              )
            ),
        })
      ),
      // Bulk Move Modal
      When(state.showBulkMoveModal, () =>
        FolderPickerModal({
          title: 'Move Files to Folder',
          message: state.selectedCount.map(
            (c) => `Move ${c} file${c > 1 ? 's' : ''} to:`
          ),
          availableFolders: state.availableFolders,
          loadingFolders: state.loadingFolders,
          performingAction: state.performingBulkAction,
          actionError: state.bulkActionError,
          onSelect: handlers.handleBulkMoveToFolder,
          onClose: () => state.showBulkMoveModal.set(false),
        })
      ),
      // Bulk Copy Modal
      When(state.showBulkCopyModal, () =>
        FolderPickerModal({
          title: 'Copy Files to Folder',
          message: state.selectedCount.map(
            (c) => `Copy ${c} file${c > 1 ? 's' : ''} to:`
          ),
          availableFolders: state.availableFolders,
          loadingFolders: state.loadingFolders,
          performingAction: state.performingBulkAction,
          actionError: state.bulkActionError,
          onSelect: handlers.handleBulkCopyToFolder,
          onClose: () => state.showBulkCopyModal.set(false),
        })
      )
    ),
  })
}
